<script setup lang="ts">
import { ref, computed, reactive, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { loadUserInfo, logoutUser, currentUser } from '@/composables/useUser'
import { setAppLocale } from '@/i18n'
import {
  BarChart3, Send, Clock, Search, Download,
  ChevronDown, User, Settings, LogOut,
  Mail, X, Check,
  Globe, PanelLeftClose, PanelLeftOpen, Zap, Shield,
  ChevronRight, CreditCard, Key, Code2, BookOpen, Loader
} from 'lucide-vue-next'
import BrandLogo from '@/components/BrandLogo.vue'
import request from '@/utils/request'
import type { AcsResult } from '@/api/types'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

interface MenuItem {
  label: string
  value: string
  icon: any
  path?: string
  badge?: string
  children?: { label: string; value: string; icon: any; path: string }[]
}

const menuItems = computed<MenuItem[]>(() => [
  { label: t('menu.dashboard'), value: 'dashboard', icon: BarChart3, path: '/' },
  { label: t('menu.sendSms'), value: 'sendSms', icon: Send, path: '/send-sms', badge: t('badge.marketing') },
  { label: t('menu.realTimeProgress'), value: 'realTimeProgress', icon: Clock, path: '/realtime-progress' },
  { label: t('menu.pendingQueue'), value: 'pendingQueue', icon: Loader, path: '/pending-queue' },
  { label: t('menu.smsSearch'), value: 'smsSearch', icon: Search, path: '/sms-search' },
  { label: t('menu.exportRecords'), value: 'exportRecords', icon: Download, path: '/export-records' },
  {
    label: t('menu.apiIntegration'), value: 'apiIntegration', icon: Code2,
    children: [
      { label: t('menu.apiDocs'), value: 'apiDocs', icon: BookOpen, path: '/api-docs' },
      { label: t('menu.apiKeys'), value: 'apiKeys', icon: Key, path: '/api-keys' },
    ],
  },
  { label: t('menu.platformSettings'), value: 'platformSettings', icon: Settings, path: '/platform-settings' },
])

const expandedMenus = ref<Set<string>>(new Set())

const isMenuActive = (item: MenuItem) => {
  if (item.path) return route.path === item.path
  if (item.children) return item.children.some(c => route.path === c.path)
  return false
}

const isMenuExpanded = (item: MenuItem) => {
  if (!item.children) return false
  if (expandedMenus.value.has(item.value)) return true
  return isMenuActive(item)
}

const toggleMenuExpand = (item: MenuItem) => {
  if (expandedMenus.value.has(item.value)) {
    expandedMenus.value.delete(item.value)
  } else {
    expandedMenus.value.add(item.value)
  }
}

const currentTitle = computed(() => {
  for (const item of menuItems.value) {
    if (item.path && route.path === item.path) return item.label
    if (item.children) {
      const child = item.children.find(c => route.path === c.path)
      if (child) return child.label
    }
  }
  return t('menu.dashboard')
})

const currentIcon = computed(() => {
  for (const item of menuItems.value) {
    if (item.path && route.path === item.path) return item.icon
    if (item.children) {
      const child = item.children.find(c => route.path === c.path)
      if (child) return child.icon
    }
  }
  return BarChart3
})

const handleMenuClick = (item: MenuItem) => {
  if (item.children) {
    toggleMenuExpand(item)
    if (!isMenuExpanded(item) && item.children.length > 0) {
      router.push(item.children[0].path)
    }
    return
  }
  if (item.path) router.push(item.path)
}

const handleSubMenuClick = (child: { path: string }) => {
  router.push(child.path)
}

const userInfo = ref({
  name: '',
  balance: '0.00',
  currency: 'USD',
  email: '',
  phone: ''
})
watch(currentUser, (u) => {
  if (u) {
    userInfo.value = {
      name: u.name,
      balance: u.balance,
      currency: u.currency,
      email: u.email,
      phone: u.raw?.userName || '',
    }
  }
}, { immediate: true })

const userDropdownOpen = ref(false)
const userBtnRef = ref<HTMLElement | null>(null)
const userDropdownStyle = reactive<Record<string, string>>({})

const toggleUserDropdown = () => {
  if (userDropdownOpen.value) {
    userDropdownOpen.value = false
    return
  }
  const btn = userBtnRef.value
  if (!btn) return
  const rect = btn.getBoundingClientRect()
  userDropdownStyle.position = 'fixed'
  userDropdownStyle.right = `${window.innerWidth - rect.right}px`
  userDropdownStyle.top = `${rect.bottom + 6}px`
  userDropdownOpen.value = true
}

const accountModalVisible = ref(false)
const changePasswordStep = ref<0 | 1 | 2>(0)
const accountEmailStep = ref<0 | 1 | 2>(0)

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const emailCountdown = ref(0)
const newEmail = ref('')
const newEmailCountdown = ref(0)

const sendVerifyCode = () => {
  emailCountdown.value = 60
  const timer = setInterval(() => {
    emailCountdown.value--
    if (emailCountdown.value <= 0) clearInterval(timer)
  }, 1000)
}

const sendNewEmailCode = () => {
  newEmailCountdown.value = 60
  const timer = setInterval(() => {
    newEmailCountdown.value--
    if (newEmailCountdown.value <= 0) clearInterval(timer)
  }, 1000)
}

const accountTab = ref<'password' | 'email'>('password')
const accountNotice = ref('')

const confirmPasswordChange = () => {
  accountNotice.value = t('account.password.notAvailable')
}

const confirmEmailChange = () => {
  accountNotice.value = t('account.email.notAvailable')
}

const openAccountSettings = () => {
  userDropdownOpen.value = false
  accountModalVisible.value = true
  accountTab.value = 'password'
  changePasswordStep.value = 0
  accountEmailStep.value = 0
  accountNotice.value = ''
  oldPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
}

const logoutConfirmVisible = ref(false)
const openLogoutConfirm = () => {
  userDropdownOpen.value = false
  logoutConfirmVisible.value = true
}

const handleLogout = async () => {
  logoutConfirmVisible.value = false
  await logoutUser()
  router.push('/login')
}

const sidebarCollapsed = ref(false)

const handleClickOutside = (e: MouseEvent) => {
  if (userDropdownOpen.value) {
    const target = e.target as HTMLElement
    if (userBtnRef.value?.contains(target)) return
    const panel = document.getElementById('user-dropdown-panel')
    if (panel?.contains(target)) return
    userDropdownOpen.value = false
  }
  if (langDropdownOpen.value) {
    const target = e.target as HTMLElement
    if (langBtnRef.value?.contains(target)) return
    const panel = document.getElementById('lang-dropdown-panel')
    if (panel?.contains(target)) return
    langDropdownOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside, true))
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside, true))

// 从后端平台配置应用主题配色（与管理端平台设置保持一致）
// 使用公开接口 /system/config/getLogo（无需权限，客户端用户可访问）
const applyThemeFromServer = () => {
  request<AcsResult<Record<string, unknown>>>({
    url: '/system/config/getLogo',
    method: 'get',
  }).then((res) => {
    const data = ((res as AcsResult<Record<string, unknown>>)?.data ?? {}) as Record<string, unknown>
    if (!data.themePrimary && !data.primary) return
    if (data.primary) document.documentElement.style.setProperty('--theme-primary', String(data.primary))
    if (data.gradient) document.documentElement.style.setProperty('--theme-gradient', String(data.gradient))
    if (data.light) document.documentElement.style.setProperty('--theme-light', String(data.light))
    if (data.medium) document.documentElement.style.setProperty('--theme-medium', String(data.medium))
    localStorage.setItem('admin.platform.defaults', JSON.stringify(data))
  }).catch(() => {})
}

const pageReady = ref(false)
onMounted(() => {
  requestAnimationFrame(() => { pageReady.value = true })
  loadUserInfo().catch(() => {})
  applyThemeFromServer()
})

const langOptions = computed(() => [
  { value: 'zh-CN', label: t('lang.zhCN') },
  { value: 'en-US', label: t('lang.enUS') },
  { value: 'ja-JP', label: t('lang.jaJP') },
])
const currentLangLabel = computed(() => langOptions.value.find(o => o.value === locale.value)?.label || t('lang.zhCN'))

const langDropdownOpen = ref(false)
const langBtnRef = ref<HTMLElement | null>(null)
const langDropdownStyle = reactive<Record<string, string>>({})

const toggleLangDropdown = () => {
  if (langDropdownOpen.value) {
    langDropdownOpen.value = false
    return
  }
  const btn = langBtnRef.value
  if (!btn) return
  const rect = btn.getBoundingClientRect()
  langDropdownStyle.position = 'fixed'
  langDropdownStyle.right = `${window.innerWidth - rect.right}px`
  langDropdownStyle.top = `${rect.bottom + 6}px`
  langDropdownOpen.value = true
}

const selectLang = (val: string) => {
  setAppLocale(val)
  langDropdownOpen.value = false
}
</script>

<template>
  <div class="client-layout" :class="{ 'page-ready': pageReady, 'sidebar-collapsed': sidebarCollapsed }">
    <aside class="sidebar anim-item" style="--anim-delay: 0">
      <div class="sidebar-header">
        <div class="sidebar-logo">
          <BrandLogo
            :size="34"
            :variant="sidebarCollapsed ? 'mark' : 'inline'"
            theme="light"
            compact
          />
        </div>
      </div>

      <nav class="sidebar-nav scrollbar-thin">
        <div class="nav-section" v-if="!sidebarCollapsed">
          <div class="nav-section-label">{{ t('nav.overview') }}</div>
        </div>
        <template v-for="(item, idx) in menuItems.slice(0, 1)" :key="item.value">
          <div
            :class="['nav-item', { active: isMenuActive(item) && !item.children }]"
            :style="{ '--item-idx': idx }"
            @click="handleMenuClick(item)"
          >
            <div class="nav-item-indicator"></div>
            <component :is="item.icon" :size="18" class="nav-item-icon" />
            <Transition name="fade-slide">
              <span v-if="!sidebarCollapsed" class="nav-item-label">{{ item.label }}</span>
            </Transition>
          </div>
        </template>

        <div class="nav-section" v-if="!sidebarCollapsed">
          <div class="nav-section-label">{{ t('nav.smsService') }}</div>
        </div>
        <template v-for="(item, idx) in menuItems.slice(1, 5)" :key="item.value">
          <div
            :class="['nav-item', { active: isMenuActive(item) && !item.children }]"
            :style="{ '--item-idx': idx + 1 }"
            @click="handleMenuClick(item)"
          >
            <div class="nav-item-indicator"></div>
            <component :is="item.icon" :size="18" class="nav-item-icon" />
            <Transition name="fade-slide">
              <span v-if="!sidebarCollapsed" class="nav-item-label">{{ item.label }}</span>
            </Transition>
            <Transition name="fade-slide">
              <span v-if="!sidebarCollapsed && item.badge" class="nav-item-badge">{{ item.badge }}</span>
            </Transition>
          </div>
        </template>

        <div class="nav-section" v-if="!sidebarCollapsed">
          <div class="nav-section-label">{{ t('nav.developConfig') }}</div>
        </div>
        <template v-for="(item, idx) in menuItems.slice(5)" :key="item.value">
          <div
            :class="['nav-item', { active: isMenuActive(item) && !item.children, 'has-children': item.children }]"
            :style="{ '--item-idx': idx + 5 }"
            @click="handleMenuClick(item)"
          >
            <div class="nav-item-indicator"></div>
            <component :is="item.icon" :size="18" class="nav-item-icon" />
            <Transition name="fade-slide">
              <span v-if="!sidebarCollapsed" class="nav-item-label">{{ item.label }}</span>
            </Transition>
            <Transition name="fade-slide">
              <ChevronRight
                v-if="!sidebarCollapsed && item.children"
                :size="14"
                class="nav-item-arrow nav-item-arrow--expand"
                :class="{ expanded: isMenuExpanded(item) }"
              />
            </Transition>
          </div>
          <Transition name="sub-menu">
            <div v-if="item.children && isMenuExpanded(item) && !sidebarCollapsed" class="nav-sub-menu">
              <div class="nav-sub-menu-line"></div>
              <div
                v-for="child in item.children"
                :key="child.value"
                :class="['nav-sub-item', { active: route.path === child.path }]"
                @click="handleSubMenuClick(child)"
              >
                <div class="nav-sub-item-dot"></div>
                <span class="nav-sub-item-label">{{ child.label }}</span>
              </div>
            </div>
          </Transition>
        </template>
      </nav>

      <div class="sidebar-bottom">
        <div v-if="!sidebarCollapsed" class="sidebar-info-card">
          <div class="sidebar-info-card-glow"></div>
          <div class="sidebar-info-icon"><Zap :size="14" /></div>
          <div class="sidebar-info-text">
            <div class="sidebar-info-title">{{ t('nav.efficientReach') }}</div>
            <div class="sidebar-info-desc">{{ t('nav.efficientReachDesc') }}</div>
          </div>
        </div>
        <div class="nav-item collapse-btn" @click="sidebarCollapsed = !sidebarCollapsed">
          <span class="collapse-icon-wrap" :class="{ rotated: sidebarCollapsed }">
            <component :is="sidebarCollapsed ? PanelLeftOpen : PanelLeftClose" :size="18" class="nav-item-icon" />
          </span>
          <Transition name="fade-slide">
            <span v-if="!sidebarCollapsed" class="nav-item-label">{{ t('nav.collapseSidebar') }}</span>
          </Transition>
        </div>
      </div>
    </aside>

    <div class="main-area">
      <header class="topbar anim-item" style="--anim-delay: 1">
        <div class="topbar-left">
          <div class="topbar-breadcrumb">
            <div class="topbar-icon">
              <component :is="currentIcon" :size="16" />
            </div>
            <h1 class="topbar-title">{{ currentTitle }}</h1>
          </div>
        </div>

        <div class="topbar-right">
          <div class="balance-chip">
            <div class="balance-icon"><Zap :size="12" /></div>
            <div class="balance-content">
              <span class="balance-label">Balance</span>
              <span class="balance-value">{{ userInfo.balance }}</span>
            </div>
            <span class="balance-currency">{{ userInfo.currency }}</span>
          </div>

          <div class="topbar-divider"></div>

          <div ref="langBtnRef" class="lang-trigger" @click="toggleLangDropdown">
            <Globe :size="15" class="lang-trigger-icon" />
            <span class="lang-trigger-label">{{ currentLangLabel }}</span>
            <ChevronDown :size="12" class="lang-trigger-arrow" :class="{ open: langDropdownOpen }" />
          </div>

          <div class="topbar-divider"></div>

          <div ref="userBtnRef" class="user-trigger" @click="toggleUserDropdown">
            <div class="user-avatar">
              {{ userInfo.name.charAt(0).toUpperCase() }}
              <span class="avatar-online"></span>
            </div>
            <span class="user-name">{{ userInfo.name }}</span>
            <ChevronDown :size="14" class="user-chevron" :class="{ open: userDropdownOpen }" />
          </div>
        </div>
      </header>

      <main class="main-content scrollbar-thin">
        <div class="content-wrapper">
          <router-view />
        </div>
      </main>
    </div>

    <Teleport to="body">
      <Transition name="dropdown">
        <div
          v-if="userDropdownOpen"
          id="user-dropdown-panel"
          class="user-dropdown"
          :style="userDropdownStyle"
        >
          <div class="dropdown-profile">
            <div class="dropdown-profile-bg"></div>
            <div class="dropdown-profile-content">
              <div class="dropdown-avatar">
                {{ userInfo.name.charAt(0).toUpperCase() }}
              </div>
              <div class="dropdown-user-info">
                <div class="dropdown-user-name">{{ userInfo.name }}</div>
                <div class="dropdown-user-email">{{ userInfo.email }}</div>
              </div>
            </div>
          </div>

          <div class="dropdown-body">
            <div class="dropdown-item" @click="openAccountSettings">
              <div class="dropdown-item-icon dropdown-item-icon--primary">
                <User :size="15" />
              </div>
              <div class="dropdown-item-text">
                <div class="dropdown-item-label">{{ t('layout.accountSettings') }}</div>
                <div class="dropdown-item-desc">{{ t('layout.accountSettingsDesc') }}</div>
              </div>
              <ChevronRight :size="14" class="dropdown-item-arrow" />
            </div>
          </div>

          <div class="dropdown-sep"></div>

          <div class="dropdown-footer">
            <div class="dropdown-logout" @click="openLogoutConfirm">
              <LogOut :size="14" />
              <span>{{ t('layout.logout') }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="dropdown">
        <div
          v-if="langDropdownOpen"
          id="lang-dropdown-panel"
          class="lang-dropdown"
          :style="langDropdownStyle"
        >
          <div class="lang-dropdown-header">
            <Globe :size="13" />
            <span>{{ t('lang.select') }}</span>
          </div>
          <div
            v-for="opt in langOptions"
            :key="opt.value"
            :class="['lang-dropdown-item', { 'lang-dropdown-item--active': locale === opt.value }]"
            @click="selectLang(opt.value)"
          >
            <span class="lang-dropdown-label">{{ opt.label }}</span>
            <Check v-if="locale === opt.value" :size="14" class="lang-dropdown-check" />
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="accountModalVisible" class="modal-overlay" @click.self="accountModalVisible = false">
          <div class="modal-container" style="max-width: 500px">
            <div class="modal-header">
              <h3 class="modal-title">{{ t('account.title') }}</h3>
              <button class="modal-close" @click="accountModalVisible = false"><X :size="16" /></button>
            </div>
            <div class="modal-body">
              <div class="account-tabs">
                <div
                  :class="['account-tab', { active: accountTab === 'password' }]"
                  @click="accountTab = 'password'; changePasswordStep = 0; accountEmailStep = 0"
                >
                  <Shield :size="14" />
                  {{ t('account.tabs.password') }}
                </div>
                <div
                  :class="['account-tab', { active: accountTab === 'email' }]"
                  @click="accountTab = 'email'; changePasswordStep = 0; accountEmailStep = 1"
                >
                  <Mail :size="14" />
                  {{ t('account.tabs.email') }}
                </div>
              </div>

              <div v-if="accountTab === 'password' && changePasswordStep === 0" class="account-section">
                <div class="account-hint">
                  <Shield :size="14" />
                  <span>{{ t('account.password.hint') }}</span>
                </div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.password.currentPassword') }}</label>
                  <input v-model="oldPassword" type="password" class="form-input" :placeholder="t('account.password.currentPasswordPlaceholder')" />
                </div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.password.newPassword') }}</label>
                  <input v-model="newPassword" type="password" class="form-input" :placeholder="t('account.password.newPasswordPlaceholder')" />
                </div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.password.confirmPassword') }}</label>
                  <input v-model="confirmPassword" type="password" class="form-input" :placeholder="t('account.password.confirmPasswordPlaceholder')" />
                </div>
                <button class="btn-primary btn-block" @click="changePasswordStep = 1">{{ t('account.password.nextStep') }}</button>
              </div>

              <div v-if="accountTab === 'password' && changePasswordStep === 1" class="account-section">
                <div class="step-indicator">
                  <div class="step-dot completed"></div>
                  <div class="step-line completed"></div>
                  <div class="step-dot active"></div>
                  <div class="step-line"></div>
                  <div class="step-dot"></div>
                </div>
                <div class="step-title">{{ t('account.password.verifyTitle') }}</div>
                <div class="step-desc">{{ t('account.password.verifyDesc', { email: userInfo.email }) }}</div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.password.verifyCode') }}</label>
                  <div class="form-input-group">
                    <input class="form-input" :placeholder="t('account.password.verifyCodePlaceholder')" />
                    <button
                      class="form-addon-btn"
                      :disabled="emailCountdown > 0"
                      @click="sendVerifyCode"
                    >
                      {{ emailCountdown > 0 ? t('account.password.countdown', { n: emailCountdown }) : t('account.password.getCode') }}
                    </button>
                  </div>
                </div>
                <button class="btn-primary btn-block" @click="confirmPasswordChange">{{ t('account.password.confirmChange') }}</button>
                <p v-if="accountNotice && accountTab === 'password'" class="account-notice">{{ accountNotice }}</p>
              </div>

              <div v-if="accountTab === 'email' && accountEmailStep === 1" class="account-section">
                <div class="current-email-row">
                  <Mail :size="15" />
                  <span>{{ t('account.email.currentEmail', { email: userInfo.email }) }}</span>
                  <span class="profile-badge profile-badge--success"><Check :size="10" />{{ t('account.email.bound') }}</span>
                </div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.email.newEmail') }}</label>
                  <input v-model="newEmail" type="email" class="form-input" :placeholder="t('account.email.newEmailPlaceholder')" />
                </div>
                <div class="form-group">
                  <label class="form-label">{{ t('account.email.verifyCode') }}</label>
                  <div class="form-input-group">
                    <input class="form-input" :placeholder="t('account.email.verifyCodePlaceholder')" />
                    <button
                      class="form-addon-btn"
                      :disabled="newEmailCountdown > 0"
                      @click="sendNewEmailCode"
                    >
                      {{ newEmailCountdown > 0 ? t('account.email.countdown', { n: newEmailCountdown }) : t('account.email.getCode') }}
                    </button>
                  </div>
                </div>
                <button class="btn-primary btn-block" @click="confirmEmailChange">{{ t('account.email.confirmChange') }}</button>
                <p v-if="accountNotice && accountTab === 'email'" class="account-notice">{{ accountNotice }}</p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="logoutConfirmVisible" class="modal-overlay" @click.self="logoutConfirmVisible = false">
          <div class="modal-container" style="max-width: 360px">
            <div class="modal-header">
              <h3 class="modal-title">{{ t('layout.logoutConfirm') }}</h3>
              <button class="modal-close" @click="logoutConfirmVisible = false"><X :size="16" /></button>
            </div>
            <div class="modal-body">
              <div class="confirm-text">{{ t('layout.logoutConfirmText') }}</div>
              <div class="confirm-actions">
                <button class="btn-secondary" @click="logoutConfirmVisible = false">{{ t('layout.cancel') }}</button>
                <button class="btn-danger" @click="handleLogout">{{ t('layout.confirm') }}</button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.client-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: var(--color-bg);
}

.anim-item {
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: calc(var(--anim-delay) * 60ms);
}
.page-ready .anim-item {
  opacity: 1;
  transform: translateY(0);
}

/* ===== Sidebar ===== */
.sidebar {
  width: var(--sidebar-width);
  flex-shrink: 0;
  background: #fafbfc;
  border-right: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  transition: width 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  z-index: var(--z-sticky);
}

.sidebar-collapsed .sidebar {
  width: var(--sidebar-collapsed-width);
}

.sidebar-header {
  height: var(--topbar-height);
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.sidebar-logo :deep(.wm-primary) {
  font-size: 15px;
}
.sidebar-logo :deep(.wm-suffix) {
  font-size: 13px;
  letter-spacing: 0.1em;
}

.logo-icon {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  background: var(--theme-gradient, var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, var(--theme-primary, #4f46e5) 100%)));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px var(--theme-light, rgba(99, 102, 241, 0.3));
  transition: box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.sidebar:hover .logo-icon {
  box-shadow: 0 2px 12px var(--theme-light, rgba(99, 102, 241, 0.4));
  transform: scale(1.02);
}

.logo-text-group {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.logo-text {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  letter-spacing: -0.3px;
}

.sidebar-nav {
  flex: 1;
  padding: 8px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-section {
  padding: 0 12px;
  margin-top: 8px;
  margin-bottom: 2px;
}

.nav-section:first-child {
  margin-top: 0;
}

.nav-section-label {
  padding: 6px 8px 4px;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.nav-item {
  height: 38px;
  margin: 1px 8px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-md);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
}

.nav-item:hover {
  color: var(--color-text);
  background: var(--theme-light, rgba(99, 102, 241, 0.04));
}

.nav-item.active {
  color: var(--color-primary);
  background: var(--color-primary-bg);
  font-weight: var(--font-weight-semibold);
}

.nav-item-indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 55%;
  border-radius: 0 3px 3px 0;
  background: var(--color-primary);
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-item.active .nav-item-indicator {
  transform: translateY(-50%) scaleY(1);
}

.nav-item-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), color 0.2s ease;
}

.nav-item:hover .nav-item-icon {
  transform: scale(1.05);
  color: var(--color-primary);
}

.nav-item.active .nav-item-icon {
  color: var(--color-primary);
}

.nav-item-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
  transition-delay: calc(var(--item-idx) * 20ms);
}

.nav-item-badge {
  font-size: 9px;
  padding: 1px 6px;
  border-radius: var(--radius-full);
  background: var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primary, #818cf8)));
  color: #fff;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.2px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.5;
  transition-delay: calc(var(--item-idx) * 20ms);
}

.nav-item-arrow {
  color: var(--color-text-placeholder);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
}

.nav-item:hover .nav-item-arrow {
  color: var(--color-text-muted);
}

.nav-item-arrow--expand {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), color 0.2s ease;
}

.nav-item-arrow--expand.expanded {
  transform: rotate(90deg);
  color: var(--color-primary);
}

.nav-sub-menu {
  overflow: hidden;
  position: relative;
  padding: 2px 0;
}

.nav-sub-menu-line {
  position: absolute;
  left: 24px;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: var(--color-border);
  border-radius: 1px;
}

.nav-sub-item {
  height: 34px;
  margin: 1px 8px 1px 24px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
}

.nav-sub-item:hover {
  color: var(--color-primary);
  background: var(--theme-light, rgba(99, 102, 241, 0.04));
}

.nav-sub-item.active {
  color: var(--color-primary);
  background: var(--color-primary-bg);
  font-weight: var(--font-weight-semibold);
}

.nav-sub-item-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-border);
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-sub-item:hover .nav-sub-item-dot {
  background: var(--color-primary-border);
}

.nav-sub-item.active .nav-sub-item-dot {
  background: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-bg);
}

.nav-sub-item-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
}

.sub-menu-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.sub-menu-leave-active {
  transition: all 0.2s ease-in;
}
.sub-menu-enter-from,
.sub-menu-leave-to {
  opacity: 0;
  max-height: 0;
}
.sub-menu-enter-to,
.sub-menu-leave-from {
  opacity: 1;
  max-height: 200px;
}

.sidebar-bottom {
  padding: 8px 0;
  border-top: 1px solid var(--color-border-light);
  position: relative;
  z-index: 2;
}

.sidebar-info-card {
  margin: 0 12px 8px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--theme-light, linear-gradient(135deg, var(--theme-light, #eef2ff) 0%, var(--theme-light, #e0e7ff) 100%));
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--color-primary-border);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.sidebar-info-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--theme-light, rgba(99, 102, 241, 0.12));
}

.sidebar-info-card-glow {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--theme-light, rgba(99, 102, 241, 0.12)), transparent 70%);
  pointer-events: none;
}

.sidebar-info-icon {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  background: var(--theme-gradient, var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primary, #4f46e5))));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px var(--theme-light, rgba(99, 102, 241, 0.3));
}

.sidebar-info-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  letter-spacing: 0.01em;
}

.sidebar-info-desc {
  font-size: 10px;
  color: var(--color-text-muted);
  margin-top: 1px;
  letter-spacing: 0.01em;
}

.collapse-btn {
  color: var(--color-text-muted) !important;
}

.collapse-btn:hover {
  color: var(--color-text-tertiary) !important;
  background: var(--color-bg-active) !important;
}

.collapse-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.collapse-icon-wrap.rotated {
  transform: rotate(180deg);
}

/* ===== Main Area ===== */
.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* ===== Topbar ===== */
.topbar {
  height: var(--topbar-height);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid transparent;
  border-image: linear-gradient(90deg, transparent, var(--color-border), transparent) 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
}

.topbar-left {
  display: flex;
  align-items: center;
}

.topbar-breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
}

.topbar-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--color-primary-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
}

.topbar-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  letter-spacing: -0.3px;
  margin: 0;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.balance-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 14px 4px 4px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--theme-light, rgba(99, 102, 241, 0.06)) 0%, rgba(129, 140, 248, 0.04) 100%);
  border: 1px solid var(--theme-light, rgba(99, 102, 241, 0.12));
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
}

.balance-chip::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, var(--theme-light, rgba(99, 102, 241, 0.08)) 0%, transparent 60%);
  opacity: 0;
  transition: opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.balance-chip:hover {
  border-color: var(--theme-light, rgba(99, 102, 241, 0.25));
  box-shadow: 0 2px 8px var(--theme-light, rgba(99, 102, 241, 0.1)), 0 0 0 1px var(--theme-light, rgba(99, 102, 241, 0.06));
  transform: translateY(-1px);
}

.balance-chip:hover::before {
  opacity: 1;
}

.balance-chip:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px var(--theme-light, rgba(99, 102, 241, 0.08));
}

.balance-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, var(--theme-primary, #818cf8) 100%));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 4px var(--theme-light, rgba(99, 102, 241, 0.3));
  transition: box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1), transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.balance-chip:hover .balance-icon {
  box-shadow: 0 2px 8px var(--theme-light, rgba(99, 102, 241, 0.4));
  transform: scale(1.05);
}

.balance-content {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  z-index: 1;
}

.balance-label {
  font-size: 9px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-muted);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
}

.balance-value {
  color: var(--color-text);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
  font-feature-settings: "tnum" 1, "zero" 0;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.balance-currency {
  color: var(--color-primary);
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.3px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--theme-light, rgba(99, 102, 241, 0.08));
  position: relative;
  z-index: 1;
}

.topbar-divider {
  width: 1px;
  height: 20px;
  background: var(--color-border);
  margin: 0 4px;
}

.lang-trigger {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: var(--radius-md);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
}

.lang-trigger:hover {
  background: var(--color-bg-active);
}

.lang-trigger-icon {
  color: var(--color-text-muted);
  flex-shrink: 0;
  transition: color 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.lang-trigger:hover .lang-trigger-icon {
  color: var(--color-primary);
}

.lang-trigger-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  white-space: nowrap;
  transition: color 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.lang-trigger:hover .lang-trigger-label {
  color: var(--color-text);
}

.lang-trigger-arrow {
  color: var(--color-text-muted);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
}

.lang-trigger-arrow.open {
  transform: rotate(180deg);
}

.lang-dropdown {
  position: fixed;
  width: 200px;
  background: var(--color-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-dropdown);
  z-index: var(--z-popover);
  overflow: hidden;
  border: 1px solid var(--color-border-light);
  padding: 4px;
}

.lang-dropdown-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 6px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.lang-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s cubic-bezier(0.16, 1, 0.3, 1), color 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.lang-dropdown-item:hover {
  background: var(--color-bg-active);
}

.lang-dropdown-item--active {
  background: var(--color-primary-bg);
  color: var(--color-primary);
}

.lang-dropdown-label {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
}

.lang-dropdown-item--active .lang-dropdown-label {
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.lang-dropdown-check {
  color: var(--color-primary);
  flex-shrink: 0;
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 3px 6px 3px 3px;
  border-radius: var(--radius-lg);
  transition: background 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.user-trigger:hover {
  background: var(--color-bg-active);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  background: var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, #7c3aed 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  position: relative;
  box-shadow: 0 1px 4px var(--theme-light, rgba(99, 102, 241, 0.2));
  transition: box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.user-trigger:hover .user-avatar {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85), 0 0 0 3.5px var(--theme-primary, #6366f1), 0 0 10px var(--theme-light, rgba(99, 102, 241, 0.25));
}

.avatar-online {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--color-success);
  border: 2px solid rgba(255, 255, 255, 0.85);
}

.user-name {
  font-size: var(--font-size-md);
  color: var(--color-text);
  font-weight: var(--font-weight-medium);
}

.user-chevron {
  color: var(--color-text-muted);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.user-chevron.open {
  transform: rotate(180deg);
}

/* ===== Main Content ===== */
.main-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--color-bg);
  background-image: radial-gradient(circle at 1px 1px, rgba(0,0,0,0.015) 1px, transparent 0);
  background-size: 24px 24px;
}

.content-wrapper {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  animation: pageEnter 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes pageEnter {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== User Dropdown ===== */
.user-dropdown {
  position: fixed;
  width: 272px;
  background: var(--color-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-dropdown);
  z-index: var(--z-popover);
  overflow: hidden;
  border: 1px solid var(--color-border-light);
}

.dropdown-profile {
  position: relative;
  padding: 16px 14px 12px;
  overflow: hidden;
}

.dropdown-profile-bg {
  position: absolute;
  inset: 0;
  background: var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, #7c3aed 100%));
  opacity: 0.04;
}

.dropdown-profile-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}

.dropdown-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  background: var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, #7c3aed 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  box-shadow: 0 2px 8px var(--theme-light, rgba(99, 102, 241, 0.25));
}

.dropdown-user-info {
  flex: 1;
  min-width: 0;
}

.dropdown-user-name {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}

.dropdown-user-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-body {
  padding: 4px 6px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.dropdown-item:hover {
  background: var(--color-bg-active);
}

.dropdown-item-icon {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-item-icon--primary {
  background: var(--color-primary-bg);
  color: var(--color-primary);
}

.dropdown-item-text {
  flex: 1;
  min-width: 0;
}

.dropdown-item-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
}

.dropdown-item-desc {
  font-size: 10px;
  color: var(--color-text-muted);
  margin-top: 1px;
}

.dropdown-item-arrow {
  color: var(--color-text-placeholder);
  flex-shrink: 0;
}

.dropdown-sep {
  height: 1px;
  background: var(--color-border-light);
  margin: 2px 12px;
}

.dropdown-footer {
  padding: 4px 6px 6px;
}

.dropdown-logout {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-error);
  transition: background 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.dropdown-logout:hover {
  background: var(--color-error-bg);
}

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-bg-overlay);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
}

.modal-container {
  background: var(--color-card);
  border-radius: var(--radius-xl);
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: var(--shadow-modal);
  border: 1px solid var(--color-border-light);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 0;
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0;
}

.modal-close {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-xs);
  border: none;
  background: var(--color-bg-active);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-muted);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-close:hover {
  background: var(--color-border);
  color: var(--color-text-secondary);
  transform: rotate(90deg);
}

.modal-body {
  padding: 18px 22px 22px;
}

/* ===== Account Modal ===== */
.account-tabs {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--color-bg-active);
  border-radius: var(--radius-md);
  margin-bottom: 18px;
}

.account-tab {
  flex: 1;
  text-align: center;
  padding: 7px 14px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.account-tab.active {
  background: var(--color-card);
  color: var(--color-primary);
  box-shadow: var(--shadow-xs);
}

.account-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.account-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: var(--radius-sm);
  background: var(--color-warning-bg);
  color: #92400e;
  font-size: var(--font-size-sm);
  border: 1px solid var(--color-warning-border);
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 6px;
}

.step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-border);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.step-dot.active {
  background: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.step-dot.completed {
  background: var(--color-success);
}

.step-line {
  width: 32px;
  height: 2px;
  background: var(--color-border);
  transition: background 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.step-line.completed {
  background: var(--color-success);
}

.step-title {
  text-align: center;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}

.step-desc {
  text-align: center;
  font-size: var(--font-size-base);
  color: var(--color-text-muted);
  margin-top: -6px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
}

.form-input {
  height: 38px;
  padding: 0 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  font-size: var(--font-size-base);
  color: var(--color-text);
  outline: none;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  width: 100%;
  background: var(--color-card);
}

.form-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.form-input::placeholder {
  color: var(--color-text-placeholder);
}

.form-input-group {
  display: flex;
  gap: 6px;
}

.form-input-group .form-input {
  flex: 1;
}

.form-addon-btn {
  flex-shrink: 0;
  padding: 0 12px;
  height: 38px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-primary);
  background: var(--color-card);
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;
}

.form-addon-btn:hover:not(:disabled) {
  background: var(--color-primary-bg);
}

.form-addon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  border-color: var(--color-border);
  color: var(--color-text-muted);
}

.btn-primary {
  height: 38px;
  border-radius: var(--radius-sm);
  border: none;
  background: var(--theme-gradient, var(--theme-gradient, linear-gradient(135deg, var(--theme-primary, #6366f1) 0%, var(--theme-primary, #4f46e5) 100%)));
  color: #fff;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 1px 4px var(--theme-light, rgba(99, 102, 241, 0.25));
  padding: 0 20px;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px var(--theme-light, rgba(99, 102, 241, 0.35));
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-block {
  width: 100%;
}

.btn-secondary {
  height: 38px;
  padding: 0 18px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-card);
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.btn-secondary:hover {
  background: var(--color-bg-active);
  border-color: var(--color-text-muted);
}

.btn-danger {
  height: 38px;
  padding: 0 18px;
  border-radius: var(--radius-sm);
  border: none;
  background: var(--color-error);
  color: #fff;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

.result-state {
  text-align: center;
  padding: 16px 0;
}

.result-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.result-icon--success {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.result-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}

.result-desc {
  font-size: var(--font-size-base);
  color: var(--color-text-muted);
  margin-top: 4px;
  margin-bottom: 18px;
}

.current-email-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-hover);
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-light);
}

.confirm-text {
  text-align: center;
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  margin-bottom: 18px;
  padding: 8px 0;
}

.confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.confirm-actions .btn-secondary,
.confirm-actions .btn-danger {
  min-width: 96px;
}

/* ===== Transitions ===== */
.fade-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.fade-slide-leave-active {
  transition: all 0.15s ease-in;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(-6px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

.dropdown-enter-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.dropdown-leave-active {
  transition: all 0.12s ease-in;
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-3px) scale(0.99);
}

.modal-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.modal-leave-active {
  transition: all 0.15s ease-in;
}
.modal-enter-from {
  opacity: 0;
}
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal-container {
  transform: scale(0.96) translateY(8px);
  opacity: 0;
}
.modal-leave-to .modal-container {
  transform: scale(0.98) translateY(4px);
  opacity: 0;
}
</style>
